/**
 * LA COPIA DE LA EMPRESA EN EL TELÉFONO.
 *
 * Se baja ENTERA —toda la historia, sin ventanas de fechas ni topes de filas—
 * cada tabla que las pantallas leen, con la sesión del usuario: la RLS deja
 * solo lo que esa persona puede ver (un trabajador, sus tareas; un encargado, lo
 * de su empresa, que luego cada pantalla recorta por sus naves como siempre).
 * Con ella `consultaLocal` contesta sin red cualquier consulta de cualquier
 * pantalla: el mapa con cualquier rango, la ficha de cualquier muestreo, el
 * historial de cualquier foco, los partes de hace un año.
 *
 * Pesa poco (medido el 10-10-2026: la empresa más grande, Nijasol, ~5 MB entre
 * todas las tablas de negocio). Lo que NO se copia, a propósito: catálogos
 * globales enormes (usos fitosanitarios 36 MB, variedades 22 MB), registros
 * internos (auditoría, errores), la tienda, el blog y el asistente. Esas
 * pantallas siguen funcionando sin red con lo que ya se vio (`redOffline`).
 *
 * Cada tabla tiene su ritmo: lo que cambia a diario (tareas, muestreos, partes,
 * movimientos) se refresca cada `CADA_VIVA_MS`; lo que casi no cambia (naves,
 * líneas, catálogos) cada `CADA_QUIETA_MS`, que es lo que más pesa (geometrías).
 */
import { supabase } from "@/integrations/supabase/client";
import { fetchAllPages } from "@/lib/fetchAllPages";
import { guardar, leer } from "./almacenOffline";
import { esperarAppQuieta, modoActivo, sinRedSegura, usuarioOffline } from "./estadoOffline";
import type { Fila } from "./consultaLocal";

export const CADA_VIVA_MS = 20 * 60 * 1000;
export const CADA_QUIETA_MS = 6 * 60 * 60 * 1000;

import { CABECERA_ESPEJO } from "./marcaDescarga";
export { CABECERA_ESPEJO };

export interface TablaEspejo {
  tabla: string;
  /** Orden TOTAL para paginar sin perder ni repetir filas. */
  orden: string[];
  clase: "viva" | "quieta";
}

const viva = (tabla: string, orden: string[] = ["id"]): TablaEspejo => ({ tabla, orden, clase: "viva" });
const quieta = (tabla: string, orden: string[] = ["id"]): TablaEspejo => ({ tabla, orden, clase: "quieta" });

/**
 * Lo que se copia. Salen de medir qué tablas lee la app (`.from(...)`, 10-10-2026)
 * y quedarse con las de negocio. Si una pantalla nueva lee una tabla que no está
 * aquí, sin red esa consulta se contesta con lo último que se vio (o no se
 * contesta): añadirla aquí la cubre entera.
 */
export const TABLAS_ESPEJO: TablaEspejo[] = [
  // Quién es quién y qué puede ver
  viva("companies"),
  viva("company_settings"),
  viva("profiles"),
  viva("user_roles"),
  viva("member_scope"),
  viva("manager_section_permissions"),
  viva("platform_settings"),
  viva("notification_preferences"),
  viva("user_guide_progress"),
  quieta("company_report_branding", ["company_id"]),
  // La finca: fincas, naves, sectores, líneas (la base del mapa)
  quieta("locations"),
  quieta("greenhouses"),
  quieta("blocks"),
  quieta("block_rows"),
  quieta("greenhouse_sigpac_recintos"),
  quieta("farm_legal_profiles"),
  viva("block_worker_assignments"),
  viva("crop_cycles"),
  quieta("campaigns"),
  // Sanidad: muestreos, observaciones, focos, alertas
  viva("pest_scouting_sessions"),
  viva("pest_scouting_observations"),
  viva("v_active_pest_sessions"),
  viva("pest_foci"),
  viva("focus_link_decisions"),
  viva("pest_photo_analyses"),
  viva("spray_alerts"),
  quieta("pests"),
  quieta("pest_catalog"),
  quieta("pest_catalog_releases", ["version"]),
  quieta("monitored_pests"),
  quieta("location_crop_pests"),
  quieta("location_own_crop_pests"),
  quieta("crop_pest_relevance"),
  // Tareas y lo que hace cada trabajador
  viva("planner_tasks"),
  viva("planner_alerts"),
  viva("quick_notes"),
  // Tratamientos y registros (cuaderno)
  quieta("treatments"),
  quieta("treatment_components"),
  quieta("treatment_pest_map"),
  viva("spray_logs"),
  viva("spray_log_entries"),
  quieta("company_equipment"),
  quieta("company_applicators"),
  viva("cuaderno_drafts"),
  quieta("cuaderno_exports"),
  // Inventario y partes de consumo (movimientos)
  viva("inventory"),
  viva("inventory_location_stock"),
  viva("inventory_transactions"),
  viva("inventory_transaction_location_allocations"),
  viva("inventory_location_usage", ["company_id", "location_id", "product_id"]),
  viva("inventory_location_not_used"),
  viva("product_requests"),
  quieta("product_suppliers"),
  viva("scanned_invoices"),
  viva("stockout_events"),
  quieta("inventory_merge_log"),
  // Avisos
  viva("notifications"),
  // Guía
  quieta("guide_steps"),
];

export const NOMBRES_ESPEJO: ReadonlySet<string> = new Set(TABLAS_ESPEJO.map((t) => t.tabla));

interface TablaGuardada {
  filas: Fila[];
  at: number;
  /** Empresa del usuario cuando se bajó (un perito puede cambiar de empresa). */
  empresa: string | null;
}

const claveTabla = (t: string) => `tabla:${t}`;

/* ─── En memoria ──────────────────────────────────────────────────────────
   Sin red, la primera consulta a una tabla la sube de IndexedDB a memoria y las
   siguientes la leen de aquí. Se vacía al cambiar de usuario o al bajar otra
   versión de la tabla. */

const memoria = new Map<string, { uid: string; filas: Fila[]; at: number }>();

export function vaciarMemoriaEspejo(): void {
  memoria.clear();
}

/** Las filas de una tabla ya cargada en memoria (o null). Síncrono, para `consultaLocal`. */
export function filasEnMemoria(tabla: string): Fila[] | null {
  const uid = usuarioOffline();
  const m = memoria.get(tabla);
  return m && m.uid === uid ? m.filas : null;
}

/**
 * Sube a memoria las tablas pedidas. Devuelve la hora de la copia MÁS VIEJA de
 * entre ellas, o null si alguna no está copiada (entonces no se puede contestar).
 */
export async function cargarTablas(tablas: string[]): Promise<number | null> {
  const uid = usuarioOffline();
  if (!uid) return null;
  let masVieja = Infinity;
  for (const t of tablas) {
    if (!NOMBRES_ESPEJO.has(t)) return null;
    let m = memoria.get(t);
    if (!m || m.uid !== uid) {
      const g = await leer<TablaGuardada>("tablas", uid, claveTabla(t));
      if (!g || !Array.isArray(g.filas)) return null;
      m = { uid, filas: g.filas, at: g.at };
      memoria.set(t, m);
    }
    masVieja = Math.min(masVieja, m.at);
  }
  return Number.isFinite(masVieja) ? masVieja : null;
}

/* ─── Descarga ───────────────────────────────────────────────────────────── */

export interface ResultadoTabla {
  tabla: string;
  filas: number;
  ok: boolean;
  saltada?: boolean;
  error?: string;
}

/** Lo que hace falta del constructor de postgrest-js (la tabla va por nombre, sin tipos). */
type Lectura = PromiseLike<{ data: Fila[] | null; error: { message?: string } | null }> & {
  order(columna: string, o?: { ascending: boolean }): Lectura;
  range(desde: number, hasta: number): Lectura;
  setHeader(nombre: string, valor: string): Lectura;
};
const leerTabla = (t: string): Lectura =>
  (supabase as unknown as { from(t: string): { select(c: string): Lectura } }).from(t).select("*");

async function bajarTabla(t: TablaEspejo): Promise<{ filas: Fila[] } | { error: string }> {
  const resultado = await fetchAllPages<Fila, { message?: string }>(
    (desde, hasta) => {
      let q = leerTabla(t.tabla);
      for (const c of t.orden) q = q.order(c, { ascending: true });
      return q.range(desde, hasta).setHeader(CABECERA_ESPEJO, "1");
    },
    { pageSize: 1000, maxPages: 200, keyOf: (f) => t.orden.map((c) => String(f[c])).join("|") },
  );
  if (resultado.error) return { error: resultado.error.message ?? "error" };
  if (resultado.capped) return { error: "demasiadas filas" };
  return { filas: resultado.rows };
}

/** ¿Le toca bajar a esta tabla? */
async function toca(uid: string, t: TablaEspejo, ahora: number, empresa: string | null): Promise<boolean> {
  const g = await leer<{ at: number; empresa: string | null }>("meta", uid, `espejo:${t.tabla}`);
  if (!g) return true;
  if (g.empresa !== empresa) return true;
  return ahora - g.at > (t.clase === "viva" ? CADA_VIVA_MS : CADA_QUIETA_MS);
}

/**
 * Baja las tablas que tocan (o todas con `forzar`). De pocas en pocas, para no
 * ahogar una red floja; se para si se pierde la red o cambia el usuario. Una
 * tabla que falla conserva su copia anterior.
 */
export async function descargarEspejo(opciones: {
  empresa: string | null;
  forzar?: boolean;
  enParalelo?: number;
  alAvanzar?: (hechas: number, total: number) => void;
}): Promise<ResultadoTabla[]> {
  const uid = usuarioOffline();
  if (!uid || !modoActivo()) return [];
  const ahora = Date.now();
  const pendientes: TablaEspejo[] = [];
  for (const t of TABLAS_ESPEJO) {
    if (opciones.forzar || (await toca(uid, t, ahora, opciones.empresa))) pendientes.push(t);
  }
  const resultados: ResultadoTabla[] = [];
  let i = 0;
  let hechas = 0;
  const trabajador = async () => {
    while (i < pendientes.length) {
      const t = pendientes[i++];
      // Primero lo que el usuario está cargando: la descarga espera a que la app esté quieta.
      await esperarAppQuieta();
      if (sinRedSegura() || usuarioOffline() !== uid || !modoActivo()) {
        resultados.push({ tabla: t.tabla, filas: 0, ok: false, saltada: true });
        continue;
      }
      try {
        const r = await bajarTabla(t);
        if ("error" in r) {
          resultados.push({ tabla: t.tabla, filas: 0, ok: false, error: r.error });
        } else if (usuarioOffline() === uid) {
          const at = Date.now();
          const ok = await guardar("tablas", uid, claveTabla(t.tabla), {
            filas: r.filas,
            at,
            empresa: opciones.empresa,
          } satisfies TablaGuardada);
          if (ok) {
            await guardar("meta", uid, `espejo:${t.tabla}`, { at, empresa: opciones.empresa, filas: r.filas.length });
            memoria.set(t.tabla, { uid, filas: r.filas, at });
          }
          resultados.push({ tabla: t.tabla, filas: r.filas.length, ok });
        }
      } catch (e) {
        resultados.push({ tabla: t.tabla, filas: 0, ok: false, error: e instanceof Error ? e.message : String(e) });
      }
      opciones.alAvanzar?.(++hechas, pendientes.length);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, opciones.enParalelo ?? 2) }, trabajador));
  return resultados;
}

/** Filas guardadas de una tabla (para la descarga de fotos y teselas). */
export async function filasGuardadas(tabla: string): Promise<Fila[]> {
  const enMemoria = filasEnMemoria(tabla);
  if (enMemoria) return enMemoria;
  const at = await cargarTablas([tabla]);
  return at === null ? [] : filasEnMemoria(tabla) ?? [];
}

/** Resumen para la ficha «Datos guardados en este móvil». */
export async function resumenEspejo(): Promise<{ tablas: number; filas: number; masVieja: number | null }> {
  const uid = usuarioOffline();
  if (!uid) return { tablas: 0, filas: 0, masVieja: null };
  let tablas = 0;
  let filas = 0;
  let masVieja: number | null = null;
  for (const t of TABLAS_ESPEJO) {
    const m = await leer<{ at: number; filas: number }>("meta", uid, `espejo:${t.tabla}`);
    if (!m) continue;
    tablas++;
    filas += m.filas ?? 0;
    masVieja = masVieja === null ? m.at : Math.min(masVieja, m.at);
  }
  return { tablas, filas, masVieja };
}
